import type { RefObject } from 'react';
import { useCallback, useRef, useState } from 'react';

import { getWindowSafe } from '@change-corgi/core/window';
import { useUtilityContext } from '@change-corgi/core-react/utilityContext';

import { usePetitionDetailsPageUserData } from '#src/app/pages/petitionDetails/pageContext';
import { treatExperiment } from '#src/app/shared/api/experiments';

import type { WatermarkPlacement } from '../../components/WatermarkedImage';

/** Matches the tailwind `md` breakpoint that the hero layout switches on. */
export const MOBILE_MEDIA_QUERY = '(max-width: 767px)';

// The AI-Badge spec, translated for baking. The mark scales with the image, so its size is the
// spec's px over the hero's design width (21px at a 390px-wide mobile hero → 5.5%; 42px at the
// 1015px desktop hero → 4%). The padding mirrors the page's fixed text/content padding, so it
// stays in CSS px and is converted at composite time via the measured rendered width. Both
// viewports use top-left (the spec's fallback when the right corners are occupied): the sign
// panel covers the hero's top-right on desktop, and the device-gated NativeShareMobile button
// sticks to the top-right on mobile devices regardless of viewport width.
const MOBILE_PLACEMENT: WatermarkPlacement = { corner: 'top-left', markWidthRatio: 0.055, paddingCss: 16 };
const DESKTOP_PLACEMENT: WatermarkPlacement = { corner: 'top-left', markWidthRatio: 0.04, paddingCss: 20 };

function pickPlacement(): WatermarkPlacement {
	const matches = getWindowSafe()?.matchMedia(MOBILE_MEDIA_QUERY).matches ?? false;
	return matches ? MOBILE_PLACEMENT : DESKTOP_PLACEMENT;
}

type Result = ModelHookResult<
	{
		/** The viewer's arm bakes the watermark into the hero. */
		watermarked: boolean;
		/**
		 * Corner and geometry for the baked mark, picked once at mount for the current viewport —
		 * deliberately without a media-query listener: crossing the breakpoint later keeps showing
		 * the already-baked image (always watermarked, just placed for the other layout) until the
		 * next page load.
		 */
		placement: WatermarkPlacement;
	},
	{
		/**
		 * Deferred, symmetric bucketing: treats once when the hero has actually painted —
		 * the watermarked image for the treatment arm, the untouched image for control arms.
		 * Never treats when compositing fails (the unmarked fallback must not enter the
		 * measured population).
		 */
		onPaint: () => void;
	}
>;

export function useAiHeroPhoto(rootRef: RefObject<HTMLElement | null>): Result {
	const utilityContext = useUtilityContext();
	const {
		data: { experiments },
	} = usePetitionDetailsPageUserData();
	const experiment = experiments.aiHeroImageWatermarkExperiment;
	// Ref, not state: the hero can report its paint twice in the same tick (an already-complete
	// image is reported from a mount effect and can still fire onLoad) — a state-based guard
	// would let both through.
	const hasTreatedRef = useRef(false);
	const [placement] = useState(pickPlacement);

	// a fallback resolution (FCM off, not-found, ...) is not a real assignment: treating would
	// enroll the user server-side (the service treats by name and knows nothing about our FCM)
	// while we rendered them the default experience
	const treatable = !experiment.fallback;

	const onPaint = useCallback(() => {
		// oxlint-disable-next-line react-hooks/refs
		if (!treatable || hasTreatedRef.current) return;
		// Both heading slots (desktop + mobile) mount their own AiHeroPhoto and CSS hides the
		// inactive one — its image still loads and would double-treat. Only the instance the
		// viewer can actually see records the treatment; offsetParent is null inside any
		// display:none ancestor. Checked before consuming the once-guard so a hidden instance
		// doesn't burn it.
		// oxlint-disable-next-line react-hooks/refs
		if (!rootRef.current || rootRef.current.offsetParent === null) return;
		hasTreatedRef.current = true;
		void treatExperiment(utilityContext, experiment.experimentName);
	}, [treatable, experiment.experimentName, utilityContext, rootRef]);

	return {
		data: {
			// a fallback resolution always carries the default (control) variation, so no extra check
			watermarked: experiment.variation === 'enabled',
			placement,
		},
		actions: { onPaint },
	};
}
